import type { FC, ReactNode } from 'react';
import {
  storageCommodityUnitLabels,
  storageFlowUnitLabels,
  storageOfferStatusLabels,
} from 'src/apis/storage/mappings';
import type {
  StorageCommodityUnit,
  StorageFlowUnit,
  StorageOffersOverviewParams,
  StorageOfferStatus,
  StorageTypeOfProduct,
} from 'src/apis/storage/types';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { FormatValue } from 'src/components/data-display/format-value';
import {
  StatusDisplay,
  type StatusProps,
} from 'src/components/data-display/status-display';

/** Default query when loading the public offers list (e.g. create-offer duplicate checks). */
export const storageOffersListDefaultParams: StorageOffersOverviewParams = {
  limit: 100,
  offset: 0,
  sortBy: 'OFFER_BUSINESS_ID',
  sortDirection: 'asc',
};

function getStorageProductQuantityUnitLabel(
  typeOfProduct: StorageTypeOfProduct
): string {
  switch (typeOfProduct.typeOfProduct) {
    case 'SBU':
      return 'SBU';
    case 'STORAGE_VOLUME':
      return storageCommodityUnitLabels[typeOfProduct.unit];
    case 'INJECTION_CAPACITY':
    case 'WITHDRAWAL_CAPACITY':
      return storageFlowUnitLabels[typeOfProduct.unit];
  }
}

export function getStorageReservePriceSuffix(
  typeOfProduct: StorageTypeOfProduct
): string {
  const unitLabel = getStorageProductQuantityUnitLabel(typeOfProduct);
  return `/${unitLabel}/Runtime`;
}

const storageOfferStatusVariantMap: Record<
  StorageOfferStatus,
  StatusProps['variant']
> = {
  OPEN: 'ready',
  FINISHED: 'success',
  DELETED: 'inactive',
  PUBLISHED: 'warning',
  CREATED: 'warning',
  WAITING_FOR_NEXT_ROUND: 'warning',
  APPROVAL_PENDING: 'warning',
  REJECTED: 'error',
  CANCELLED: 'inactive',
  FAILED: 'error',
};

// TODO: to be renamed to StorageOfferStatusWithTimer once the we support the countdown timer
export const StorageOfferStatusDisplay: FC<{
  status: StorageOfferStatus;
}> = ({ status }) => (
  <StatusDisplay
    variant={storageOfferStatusVariantMap[status]}
    status={storageOfferStatusLabels[status]}
  />
);

export function formatCommodityDisplay(
  value: { amount: number; unit: StorageCommodityUnit } | null | undefined
): ReactNode {
  if (!value) {
    return <EmptyValue />;
  }
  return (
    <>
      <FormatValue
        value={value.amount}
        label=""
        hideLabel
        type="exact-decimals"
      />{' '}
      {storageCommodityUnitLabels[value.unit]}
    </>
  );
}

export function formatCapacityDisplay(
  value: { amount: number; unit: StorageFlowUnit } | null | undefined
): ReactNode {
  if (!value) {
    return <EmptyValue />;
  }
  return (
    <>
      <FormatValue
        value={value.amount}
        label=""
        hideLabel
        type="exact-decimals"
      />{' '}
      {storageFlowUnitLabels[value.unit]}
    </>
  );
}
